import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import Spinner from 'components/Spinner'
import TooltipLabel from 'components/TooltipLabel'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import isEmpty from 'lodash/isEmpty'
import { CouponPoolGenerationType, CouponPoolType } from 'models/CouponPool'
import { Fields as RedeemTemplate } from 'models/RedeemTemplate'
import { RedemptionProvider } from 'models/RedemptionProvider'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { useFormModel } from 'scenes/RedeemManager/Builder/FormModel'
import useCouponPools from 'scenes/RedeemManager/Builder/useCouponPools'
import {
  couldHaveDiscountedProducts,
  isDirectPosIntegration,
  isFixedPriceKey,
  isFreeVariantKey,
  shouldHaveRequiredProducts,
} from 'scenes/RedeemManager/helpers'
import { RedeemTypeItem } from 'scenes/RedeemManager/types'
import { selectCouponPoolsByTemplateId } from 'selectors/couponPool'
import ModifierOrProductInput from '../../ModifierOrProductInput'
import ConditionalProductInput from './ConditionalProductInput'

type ThanxGeneratedEnabledProps = {
  typeItem: RedeemTypeItem | null
  template: RedeemTemplate | null
  redemptionProvider?: RedemptionProvider | null
  // Rendered inside Configure POS, whose form edits posFields[provider]
  // rather than the base form model.
  isProviderForm?: boolean
}

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.instore_redemption_card.coupon_codes'
)

const ThanxGeneratedEnabled = ({
  typeItem,
  template,
  redemptionProvider,
  isProviderForm = false,
}: ThanxGeneratedEnabledProps) => {
  const { instore_scanning_enabled: instoreScanningEnabled } =
    useCurrentMerchant() || {}
  const couponPools = useSelector(state => {
    if (template?.id) {
      return selectCouponPoolsByTemplateId(state, template.id)
    }
  })
  const [isLoading, setIsLoading] = useState(true)

  const freeModifierRewardsEnabled =
    useFlag('mx-free-modifier-rewards', false) &&
    (typeItem?.key === 'get_a_free_modifier' ||
      typeItem?.key === 'get_a_free_item')

  const isFixedPriceType = isFixedPriceKey(typeItem?.key)

  const filterCouponPools = () => {
    if (!couponPools || !redemptionProvider) return null

    return couponPools.filter(
      pool => pool.redemption_provider?.value === redemptionProvider.value
    )[0]
  }

  const selectCouponPool = () => {
    if (!couponPools) return null
    return redemptionProvider ? filterCouponPools() : couponPools[0]
  }

  const couponPool = !!couponPools?.length ? selectCouponPool() : null
  const isFree =
    typeItem?.types.includes('manual/item') || isFreeVariantKey(typeItem?.key)
  const redemptionProviderValue = redemptionProvider?.value || ''
  const inStoreProviderValues =
    template?.product_ids?.[redemptionProviderValue]?.instore
  const inStoreValues = template?.product_ids?.instore
  const subtype = template?.subtype

  const getDiscountedProductIds = () => {
    if (redemptionProvider && subtype === 'modifier')
      return inStoreProviderValues?.discounted_related || []
    if (redemptionProvider) return inStoreProviderValues?.discounted || []
    if (subtype === 'modifier') return inStoreValues?.discounted_related || []
    return inStoreValues?.discounted || []
  }

  const getDiscountedModifierIds = () => {
    if (redemptionProvider && subtype === 'modifier')
      return inStoreProviderValues?.discounted || []
    if (redemptionProvider)
      return inStoreProviderValues?.discounted_related || []
    if (subtype === 'modifier') return inStoreValues?.discounted || []
    return inStoreValues?.discounted_related || []
  }

  const requiredProductIds = redemptionProvider
    ? inStoreProviderValues?.required || []
    : inStoreValues?.required || []
  const discountedProductIds = getDiscountedProductIds()
  const discountedModifierIds = getDiscountedModifierIds()

  const displayRequiredProducts =
    shouldHaveRequiredProducts(template?.type, template?.subtype) ||
    (template?.subtype === null && !isEmpty(requiredProductIds))
  const displayDiscountedProducts =
    couldHaveDiscountedProducts(template?.type, template?.subtype) ||
    (template?.subtype === null && !isEmpty(discountedProductIds))
  const couponPoolType = instoreScanningEnabled
    ? CouponPoolType.QRCODE
    : CouponPoolType.RAW
  const isDirect = isDirectPosIntegration(redemptionProvider)

  const {
    model: formModel,
    setModel: setFormModel,
    commitModel,
  } = useFormModel()
  // Read from the form model, not the saved template, so modifier discounts
  // prefilled by the discount scope radio show before the autosave lands.
  const providerFields =
    isProviderForm && redemptionProvider
      ? formModel.posFields[redemptionProvider.value]
      : undefined
  const modifierDiscountSource = providerFields ?? formModel
  const modifierDiscountIds =
    modifierDiscountSource?.instoreModifierDiscountIds ?? []
  const modifierDiscountsAdvanced = Boolean(
    modifierDiscountSource?.instoreModifierDiscountsAdvanced
  )

  // Entering or leaving advanced mode starts the modifier discounts from the
  // restrictions. Configure POS saves on its own button, so provider changes
  // only update the model; the base form autosaves.
  const setModifierDiscountsAdvanced = (advanced: boolean) => {
    const fields = {
      instoreModifierDiscountsAdvanced: advanced,
      instoreModifierDiscountIds: [
        ...(modifierDiscountSource?.instoreDiscountedModifierIds ?? []),
      ],
    }
    if (providerFields && redemptionProvider) {
      setFormModel({
        ...formModel,
        posFields: {
          ...formModel.posFields,
          [redemptionProvider.value]: { ...providerFields, ...fields },
        },
      })
      return
    }
    ;(commitModel ?? setFormModel)({ ...formModel, ...fields })
  }
  const { createCouponPool } = useCouponPools()

  useEffect(() => {
    async function fetchCouponPools() {
      if (!template?.id) return
      setIsLoading(true)
      await createCouponPool({
        templateId: template.id,
        generationType: CouponPoolGenerationType.DYNAMIC,
        type: couponPoolType,
        returnExistingPool: true,
        redemptionProvider: redemptionProvider,
      })
      setIsLoading(false)
    }
    fetchCouponPools()
  }, [createCouponPool, template?.id, redemptionProvider, couponPoolType])

  useEffect(() => {
    if (!!couponPools?.length) {
      const source = redemptionProvider
        ? formModel.posFields[redemptionProvider.value]
        : formModel
      source.selectedCouponPools = [couponPool]
    }
  }, [couponPools, couponPool, formModel, redemptionProvider])

  const tooltipSections = [
    {
      title: t('type_tooltip.pool_title'),
      content: t('type_tooltip.pool_content'),
    },
    {
      title: t('type_tooltip.reusable_title'),
      content: t('type_tooltip.reusable_content'),
    },
    {
      title: t('type_tooltip.unique_title'),
      content: t('type_tooltip.unique_content_1'),
    },
    {
      title: '',
      content: t('type_tooltip.unique_content_2'),
    },
  ]

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      <div className="w-100 pl-l mt-s mb-m">
        <TooltipLabel
          className="mb-xs font-size-14 bold"
          label={t('type_label')}
          sections={tooltipSections}
        />
        <Text.BodyText3 className="mb-xs" color="grey70" tag="div">
          {t('type.dynamic')}
        </Text.BodyText3>
        <Text.BodyText4 className="mb-m" color="grey70" tag="div">
          <span
            dangerouslySetInnerHTML={{
              __html: isDirect
                ? t('dynamic.entered_by_customer')
                : t('dynamic.issued_5_digit_code'),
            }}
          />
        </Text.BodyText4>

        {/*
          Product/modifier ids should be shown if the coupon generation type
          is dynamic and also if it's not a reward applied to entire
          purchase.
          Dynamic types should only have one pool, so we pick the
          first one
        */}
        <Switch
          condition={
            couponPool?.generation_type === CouponPoolGenerationType.DYNAMIC &&
            template?.subtype !== 'purchase'
          }
        >
          <Switch condition={Boolean(subtype !== 'modifier')}>
            <ConditionalProductInput
              couponPool={couponPool}
              displayRequiredProducts={displayRequiredProducts}
              requiredProductIds={requiredProductIds}
              isFree={isFree}
              displayDiscountedProducts={displayDiscountedProducts}
              discountedProductIds={discountedProductIds}
              discountedModifierIds={discountedModifierIds}
              modifierDiscountIds={modifierDiscountIds}
              modifierDiscountsAdvanced={modifierDiscountsAdvanced}
              onModifierDiscountsAdvancedChange={setModifierDiscountsAdvanced}
              displayFreeModifiers={
                freeModifierRewardsEnabled || isFixedPriceType
              }
              redeemType={typeItem?.key}
              discountScope={formModel.discountScope}
            />
          </Switch>

          <Switch condition={Boolean(subtype === 'modifier')}>
            <ModifierOrProductInput
              label={t('discounted_free_modifier_items_title')}
              secondaryText={t('discounted_label_free_modifier_items')}
              // This instoreDiscountedProductIds name is somewhat misleading, as it represents
              // the 'discounted_related' attribute for the modifiers
              name="instoreDiscountedProductIds"
              value={discountedProductIds}
              validations={{ isRequired: true }}
              validationErrors={{
                isRequired: I18n.t('validations.is_required'),
              }}
            />
            <div className="mt-m">
              <ModifierOrProductInput
                label={t('discounted_free_modifier_title')}
                secondaryText={t(
                  isFree ? 'discounted_label_free_modifier' : 'discounted_label'
                )}
                name="instoreDiscountedModifierIds"
                value={discountedModifierIds}
                validations={{ isRequired: true }}
                validationErrors={{
                  isRequired: I18n.t('validations.is_required'),
                }}
                shouldDisplayInstoreValidationNote={true}
              />
              <p className="mt-xs body-text-4 grey-70">
                {t('will_apply_lowest_free_modifier')}
              </p>
            </div>
          </Switch>
        </Switch>

        <Switch
          condition={displayRequiredProducts && !displayDiscountedProducts}
        >
          <p className="body-text-14 bold">{t('then_entire_purchase')}</p>
        </Switch>
      </div>
    </>
  )
}

export default ThanxGeneratedEnabled
